'use client';

import React, { useState } from 'react';
import { Search, Eye, TrendingUp, CreditCard, ShoppingCart, Star, Phone, MapPin, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import StatusBadge from '@/components/ui/StatusBadge';
import MetricCard from '@/components/ui/MetricCard';

interface RetailerProfile {
  id: string;
  name: string;
  phone: string;
  zone: string;
  totalOrders: number;
  creditLimit: number;
  outstanding: number;
  avgPurchase: number;
  lastOrder: string;
  creditScore: 'excellent' | 'good' | 'fair' | 'poor';
  status: 'Active' | 'Suspended';
  orderHistory: { id: string; date: string; amount: number; status: string }[];
}

const retailers: RetailerProfile[] = [
  { id: 'r1', name: 'Snack Atlas Guéliz', phone: '+212 661-112233', zone: 'Centre_Ville', totalOrders: 48, creditLimit: 8000, outstanding: 2400, avgPurchase: 1240, lastOrder: '2026-05-25', creditScore: 'excellent', status: 'Active', orderHistory: [{ id: 'o1', date: '2026-05-25', amount: 1240, status: 'Delivered_Synced' }, { id: 'o2', date: '2026-05-18', amount: 980, status: 'Delivered_Synced' }, { id: 'o3', date: '2026-05-10', amount: 1560, status: 'Delivered_Synced' }] },
  { id: 'r2', name: 'Café Majorelle', phone: '+212 662-445566', zone: 'Centre_Ville', totalOrders: 32, creditLimit: 6000, outstanding: 3200, avgPurchase: 890, lastOrder: '2026-05-24', creditScore: 'good', status: 'Active', orderHistory: [{ id: 'o4', date: '2026-05-24', amount: 890, status: 'Delivered_Synced' }, { id: 'o5', date: '2026-05-15', amount: 1100, status: 'Delivered_Synced' }] },
  { id: 'r3', name: 'Épicerie Bab Jdid', phone: '+212 663-778899', zone: 'Ville_Haute', totalOrders: 21, creditLimit: 4000, outstanding: 3800, avgPurchase: 650, lastOrder: '2026-05-20', creditScore: 'fair', status: 'Active', orderHistory: [{ id: 'o6', date: '2026-05-20', amount: 650, status: 'Delivered_Synced' }, { id: 'o7', date: '2026-05-08', amount: 720, status: 'Delivered_Synced' }] },
  { id: 'r4', name: 'Boulangerie Menara', phone: '+212 664-001122', zone: 'Khwazat', totalOrders: 15, creditLimit: 3000, outstanding: 3000, avgPurchase: 420, lastOrder: '2026-05-15', creditScore: 'poor', status: 'Suspended', orderHistory: [{ id: 'o8', date: '2026-05-15', amount: 420, status: 'Delivered_Synced' }] },
  { id: 'r5', name: 'Restaurant Fès Marrakech', phone: '+212 665-334455', zone: 'Centre_Ville', totalOrders: 56, creditLimit: 12000, outstanding: 4500, avgPurchase: 2650, lastOrder: '2026-05-26', creditScore: 'excellent', status: 'Active', orderHistory: [{ id: 'o9', date: '2026-05-26', amount: 2650, status: 'Loading' }, { id: 'o10', date: '2026-05-19', amount: 3100, status: 'Delivered_Synced' }] },
];

const scoreColors: Record<string, string> = {
  excellent: 'text-success bg-success/10',
  good: 'text-info bg-info/10',
  fair: 'text-warning bg-warning/10',
  poor: 'text-danger bg-danger/10',
};

const scoreLabels: Record<string, { ar: string; fr: string }> = {
  excellent: { ar: 'ممتاز', fr: 'Excellent' },
  good: { ar: 'جيد', fr: 'Bon' },
  fair: { ar: 'متوسط', fr: 'Moyen' },
  poor: { ar: 'ضعيف', fr: 'Faible' },
};

export default function CRMContent() {
  const { language, dir } = useLanguage();
  const t = getTranslations(language);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RetailerProfile | null>(null);

  const filtered = retailers.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.zone.toLowerCase().includes(search.toLowerCase())
  );

  const totalOutstanding = retailers.reduce((s, r) => s + r.outstanding, 0);
  const totalCredit = retailers.reduce((s, r) => s + r.creditLimit, 0);
  const avgPurchaseAll = Math.round(retailers.reduce((s, r) => s + r.avgPurchase, 0) / retailers.length);

  return (
    <div className="space-y-6 fade-in" dir={dir}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-700 text-foreground">{t('crm.title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('crm.subtitle')}</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label={language === 'ar' ? 'إجمالي التجار' : 'Total Détaillants'} value={String(retailers.length)} subValue={language === 'ar' ? `${retailers.filter(r => r.status === 'Active').length} نشط` : `${retailers.filter(r => r.status === 'Active').length} actifs`} variant="positive" icon={<ShoppingCart size={18} />} />
        <MetricCard label={language === 'ar' ? 'إجمالي الديون' : 'Total Dettes'} value={totalOutstanding.toLocaleString()} subValue={`${t('common.mad')} — ${Math.round(totalOutstanding / totalCredit * 100)}% ${language === 'ar' ? 'من الكريدي' : 'du crédit'}`} variant="warning" icon={<CreditCard size={18} />} />
        <MetricCard label={language === 'ar' ? 'إجمالي الكريدي' : 'Crédit Total'} value={totalCredit.toLocaleString()} subValue={t('common.mad')} variant="info" icon={<TrendingUp size={18} />} />
        <MetricCard label={language === 'ar' ? 'متوسط الشراء' : 'Achat Moyen'} value={avgPurchaseAll.toLocaleString()} subValue={t('common.mad')} variant="positive" icon={<Star size={18} />} />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground ${dir === 'rtl' ? 'right-3' : 'left-3'}`} />
        <input
          className={`input-base ${dir === 'rtl' ? 'pr-9' : 'pl-9'}`}
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className={`px-4 py-3 font-600 text-muted-foreground ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('crm.retailer')}</th>
                <th className={`px-4 py-3 font-600 text-muted-foreground ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('common.zone')}</th>
                <th className="px-4 py-3 font-600 text-muted-foreground text-center">{t('crm.orders')}</th>
                <th className="px-4 py-3 font-600 text-muted-foreground text-center">{t('crm.credit_limit')}</th>
                <th className="px-4 py-3 font-600 text-muted-foreground text-center">{t('crm.outstanding')}</th>
                <th className="px-4 py-3 font-600 text-muted-foreground text-center">{t('crm.avg_purchase')}</th>
                <th className="px-4 py-3 font-600 text-muted-foreground text-center">{t('crm.credit_score')}</th>
                <th className="px-4 py-3 font-600 text-muted-foreground text-center">{t('common.status')}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
                        {r.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-600 text-foreground text-sm">{r.name}</p>
                        <p className="text-2xs text-muted-foreground flex items-center gap-1">
                          <Phone size={10} /> {r.phone}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin size={11} /> {r.zone.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-tabular font-600">{r.totalOrders}</td>
                  <td className="px-4 py-3 text-center font-tabular text-info font-600">{r.creditLimit.toLocaleString()} {t('common.mad')}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-tabular font-600 ${r.outstanding / r.creditLimit > 0.8 ? 'text-danger' : r.outstanding / r.creditLimit > 0.5 ? 'text-warning' : 'text-foreground'}`}>
                      {r.outstanding.toLocaleString()} {t('common.mad')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-tabular">{r.avgPurchase.toLocaleString()} {t('common.mad')}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-2xs font-600 px-2 py-0.5 rounded-full ${scoreColors[r.creditScore]}`}>
                      {scoreLabels[r.creditScore][language]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelected(r)}
                      className="flex items-center gap-1 text-xs text-primary font-600 hover:underline"
                    >
                      <Eye size={14} />
                      {t('crm.view_profile')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Profile Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-card border border-border rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-700 text-foreground">{selected.name}</h2>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-xl p-3">
                  <p className="text-2xs text-muted-foreground mb-1">{t('crm.credit_limit')}</p>
                  <p className="font-700 text-info font-tabular">{selected.creditLimit.toLocaleString()} {t('common.mad')}</p>
                </div>
                <div className="bg-muted/50 rounded-xl p-3">
                  <p className="text-2xs text-muted-foreground mb-1">{t('crm.outstanding')}</p>
                  <p className={`font-700 font-tabular ${selected.outstanding / selected.creditLimit > 0.8 ? 'text-danger' : 'text-warning'}`}>{selected.outstanding.toLocaleString()} {t('common.mad')}</p>
                </div>
                <div className="bg-muted/50 rounded-xl p-3">
                  <p className="text-2xs text-muted-foreground mb-1">{t('crm.avg_purchase')}</p>
                  <p className="font-700 text-foreground font-tabular">{selected.avgPurchase.toLocaleString()} {t('common.mad')}</p>
                </div>
                <div className="bg-muted/50 rounded-xl p-3">
                  <p className="text-2xs text-muted-foreground mb-1">{t('crm.credit_score')}</p>
                  <span className={`text-xs font-700 px-2 py-0.5 rounded-full ${scoreColors[selected.creditScore]}`}>
                    {scoreLabels[selected.creditScore][language]}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="font-600 text-sm text-foreground mb-3">{t('crm.order_history')}</h3>
                <div className="space-y-2">
                  {selected.orderHistory.map((o) => (
                    <div key={o.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-xs font-600 text-foreground">{o.date}</p>
                        <StatusBadge status={o.status as any} />
                      </div>
                      <p className="font-tabular font-700 text-foreground">{o.amount.toLocaleString()} {t('common.mad')}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
